"use client";

import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default font-bold uppercase text-sm tracking-tighter text-foreground hover:text-secondary border-b-2 border-transparent hover:border-secondary transition-colors py-2">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const toggleDropdown = (id: string) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
          {item.children?.length ? <>
              <Button variant="ghost" onClick={() => toggleDropdown(item.id)} className="justify-between w-full px-0 font-default font-bold uppercase text-lg text-foreground hover:text-secondary hover:bg-transparent border-b border-border pb-2 transition-colors h-auto">
                {item.label}
                <ChevronDown className={`size-5 transition-transform duration-300 ${openDropdown === item.id ? "rotate-180" : ""}`} />
              </Button>
              <div className={`flex flex-col gap-3 pl-4 overflow-hidden transition-all duration-300 ${openDropdown === item.id ? "max-h-60 mt-4 mb-2 opacity-100" : "max-h-0 opacity-0"}`}>
                {item.children.map(sub => <Link key={sub.id} to={sub.href || "#"} onClick={onClose} className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors">
                    {sub.label}
                  </Link>)}
              </div>
            </> : <Link to={item.href || "#"} onClick={onClose} className="font-default font-bold uppercase text-lg text-foreground hover:text-secondary border-b border-border pb-2 transition-colors">
              {item.label}
            </Link>}
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264789" id="header" className="w-full flex flex-col sticky top-0 z-50 bg-background text-foreground">
      <div className={`static w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-lg" : "bg-background"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between !bg-[rgba(0,0,0,1)]">
          <Link to="/">
            <WvcLogo className="h-12 w-auto" />
          </Link>

          <MenuProvider menu_id="46" className="flex items-center">
            <DesktopMenuConsumer />

            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(true)} aria-label="Open Menu" className="inline-flex items-center justify-center rounded-md size-9 text-foreground !bg-[rgba(53,59,65,0.51)] hover:bg-accent">
                <Menu className="size-6" />
              </button>
            </div>

            {mobileMenuOpen && <>
                <div className="fixed inset-0 z-[998] bg-black/50" onClick={() => setMobileMenuOpen(false)} />
                <div className="fixed inset-y-0 left-0 z-[999] w-full bg-background shadow-lg flex flex-col pt-6 px-6">
                  <div className="flex items-center justify-between mb-4">
                    <WvcLogo className="h-10 w-auto" />
                    <button onClick={() => setMobileMenuOpen(false)} aria-label="Close Menu" className="rounded-md p-2 hover:bg-accent text-foreground">
                      <X className="size-5" />
                    </button>
                  </div>
                  <nav className="flex-1 overflow-y-auto">
                    <MobileMenuConsumer onClose={() => setMobileMenuOpen(false)} />
                  </nav>
                </div>
              </>}
          </MenuProvider>
        </div>
      </div>
    </section>;
}